import { LocaleProvider } from '@/components/locale-provider';
import { PortalRootProvider, SurgeProvider } from '@intercom/surge';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeType } from '@/utils/theme';
import { ThemeProvider } from '@/components/core/theme-provider';
import { RootElementProvider } from '@/providers/root-element';
import { useCurrentLocale } from '@/hooks/bridge';
import type { LocaleType } from '@/utils/locale';

export interface ProvidersProps {
  queryClient: QueryClient;
  children: React.ReactNode;
  theme: ThemeType;
  locale?: LocaleType;
}

function SurgeProviderWithTranslations({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: ThemeType;
}) {
  const currentLocale = useCurrentLocale();
  const { t } = useLingui();

  return (
    <SurgeProvider theme={theme} t={t} locale={currentLocale}>
      <PortalRootProvider>{children}</PortalRootProvider>
    </SurgeProvider>
  );
}

export const Providers = ({ queryClient, children, theme, locale }: ProvidersProps) => {
  return (
    <RootElementProvider>
      <ThemeProvider value={{ theme }}>
        <QueryClientProvider client={queryClient}>
          <LocaleProvider locale={locale}>
            <SurgeProviderWithTranslations theme={theme}>{children}</SurgeProviderWithTranslations>
          </LocaleProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </RootElementProvider>
  );
};
